import { gql, useQuery } from '@apollo/client';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import useAppSelector from '../../app/redux/useAppSelector';
import { BEACON_POST_TOPIC_BAR } from '../../constants/onboarding';
import type {
  PostTopicBar_PostFragment,
  PostTopicBarEditingQuery,
  PostTopicBarEditingQueryVariables,
  PostTopicListWithBreadcrumbs_TopicFragment,
} from '../../graphql/generated/graphqlTypes';
import TutorialBeacon from '../../onboarding/tutorial/TutorialBeacon';
import NewTopicModal from '../../topic/modal/NewTopicModal';
import { useAllTopicMap } from '../../topic/useAllTopics';
import UIActions from '../../ui/UIActions';
import populateObjects from '../../utils/populateObjects';
import {
  useAddTopicToPost,
  useRemoveTopicFromPost,
} from '../sharing/postPermissionOperations';
import useCurrentPostTopicIds from '../useCurrentPostTopicIds';
import PostTopicListWithBreadcrumbs from './PostTopicListWithBreadcrumbs';
import PostTopicToast, { shouldShowToast } from './PostTopicToast';

const POST_FRAGMENT = gql`
  fragment PostTopicBar_post on Post {
    id
    publishedAt
  }
`;

const EDIT_POST_QUERY = gql`
  query PostTopicBarEditing($id: ID!) {
    post(id: $id) {
      id
      ...useRemoveTopicFromPost_post
    }
  }
  ${useRemoveTopicFromPost.fragments.post}
`;

type EventHandlerTopic = Parameters<
  typeof shouldShowToast
>[number]['newTopic'] &
  React.ComponentProps<typeof PostTopicToast>['topic'];

interface PostTopicBarProps {
  editing: boolean;
  post: PostTopicBar_PostFragment;
}

const PostTopicBar = ({ post, editing }: PostTopicBarProps) => {
  const postTopicIds = useCurrentPostTopicIds({ subscribeToMore: true });

  const allTopicMap = useAllTopicMap();
  const postTopics = populateObjects(allTopicMap, postTopicIds);

  const toastsRef = useRef<string[]>([]);
  const dispatch = useDispatch();
  const organizationName = useAppSelector(
    (state) => state.session.organization.name,
  );

  const { data } = useQuery<
    PostTopicBarEditingQuery,
    PostTopicBarEditingQueryVariables
  >(EDIT_POST_QUERY, {
    skip: !editing,
    variables: { id: post.id },
  });
  const postForEditing = data?.post;

  const removeTopic = useRemoveTopicFromPost();
  const addTopic = useAddTopicToPost();

  const dismissToasts = useCallback(
    () =>
      Promise.all(
        toastsRef.current.map((id) => dispatch(UIActions.removeToast(id))),
      ),
    [dispatch],
  );

  const getWarningCallback = async (
    topic: EventHandlerTopic,
    adding: boolean,
  ) => {
    if (
      postTopics &&
      shouldShowToast({
        oldTopics: postTopics,
        adding,
        newTopic: topic,
        organizationName,
        published: !!post.publishedAt,
      })
    ) {
      await dismissToasts();
      // currently our redux typing doesn't consider thunks
      // eslint-disable-next-line  @typescript-eslint/await-thenable
      const id = (await dispatch(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        UIActions.pushToast(({ onDismiss }: { onDismiss: () => any }) => (
          <PostTopicToast
            topic={topic}
            adding={adding}
            addTopic={({ id: topicId }: { id: string }) =>
              addTopic(post.id, topicId)
            }
            removeTopic={(t: Parameters<typeof removeTopic>[1]) =>
              postForEditing && removeTopic(postForEditing, t)
            }
            organizationName={organizationName}
            published={!!post.publishedAt}
            postTopics={postTopics}
            onDismiss={onDismiss}
          />
        )),
      )) as unknown;
      toastsRef.current.push(id as string);
    }
  };

  useEffect(
    () => () => {
      dismissToasts();
    },
    [dismissToasts],
  );

  const handleAdd = async (topic: EventHandlerTopic) => {
    await addTopic(post.id, topic.id);
    getWarningCallback(topic, true);
  };

  const handleRemove = async (
    topic: PostTopicListWithBreadcrumbs_TopicFragment,
  ) => {
    if (!postTopics) return;

    if (postForEditing) {
      await removeTopic(postForEditing, topic);
    }
    getWarningCallback(topic, false);
  };

  const handleCreate = (name: string) => {
    dispatch(
      UIActions.openModal(NewTopicModal, {
        name,
        onCreate: handleAdd,
      }),
    );
  };
  const isEditing = editing && !!postForEditing;

  return postTopics && (postTopics.length > 0 || isEditing) ? (
    <TutorialBeacon id={BEACON_POST_TOPIC_BAR} placement="left">
      {(beaconProps) => (
        <div {...beaconProps}>
          <PostTopicListWithBreadcrumbs
            postTopics={postTopics}
            editing={isEditing}
            onAdd={handleAdd}
            onCreate={handleCreate}
            onRemove={handleRemove}
          />
        </div>
      )}
    </TutorialBeacon>
  ) : null;
};

PostTopicBar.fragments = { post: POST_FRAGMENT };

export default PostTopicBar;
